import { useTranslation } from 'react-i18next';
import { mt } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import {
  type SimpleEvent,
  useSimpleEventSubscription
} from '@passionware/simple-event';
import { ArrowDown, ArrowUp, Layers, MoreHorizontal, X } from 'lucide-react';
import {
  Fragment,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useReducedMotion } from 'motion/react';
import { flushSync } from 'react-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import type {
  AnySpec,
  KindMap,
  KindOf,
  SpecOf
} from 'v5/core/form-schema/formKinds.types';
import type { ListUnit } from 'v5/core/form-schema/formSchema.types';
import { rowIdUtils } from 'v5/core/form-schema/formRows';
import { Button } from 'v5/design-system/Button';
import { Form } from 'v5/design-system/Form';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import { OverflowTooltip } from 'v5/design-system/Tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import { DndList } from 'v5/patterns/dnd/DndList';
import { cn } from 'v5/platform/dom/cn';
import { revealWhenSettled } from 'v5/platform/dom/whenLayoutSettles';
import { AnimatedHeight } from 'v5/primitives/AnimatedHeight';
import { FieldContextProvider, useFieldContext } from './FieldContext';
import { EmptyValueMark } from './fields/EmptyField';
import { useFieldsResolver } from './fieldValidation';
import { Block, BlockFields, ListBlocksField } from './ListBlocksField';
import {
  fromListFormRows,
  type ListFormRow,
  newListFormRow,
  readRowValues,
  toListFormRows
} from './listFormRows';
import type { ListUnitViewProps } from './ListUnitView';
import { RenderField } from './RenderField';
import type { ListItemValues } from 'v5/core/form-schema/formRows';
import { useExclusiveEdit } from 'v5/patterns/forms/exclusive-edit/ExclusiveEditContext';
import type { FormKit } from './schemaForm.types';
import { UnitAddButton } from './UnitAddButton';
import { SchemaUnitEditor } from './SchemaUnitEditor';
import { UnitFoldTitle } from './UnitFoldTitle';
import { UnitTitleIcon } from './UnitTitle';
import { useUnitTransition } from './unitMotion';
import { fieldPath, isUnitOrEntry, listEntryId } from './unitPath';
import { kindHeading } from './ListLinesField';

/** One entry's form: the list form's shape with a single row, so the list's helpers read it. */
type EntryFormValues<M extends KindMap<M>> = { items: ListFormRow<M>[] };

/**
 * A list whose entries are too big to edit all at once (rates, field groups
 * with several fields): each entry is edited on its own, with its own Save,
 * from the pencil beside it; the bin removes it after asking, and the
 * header's “+” opens a new entry at the end. The order changes only in its
 * own edit mode (the header's reorder button), never by a stray drag at
 * rest. Every change writes the whole list once.
 */
export function ListEntriesUnitView<M extends KindMap<M>>({
  unit,
  binding,
  kit,
  variant,
  size,
  foldSource,
  editUnitSignal,
  icon
}: ListUnitViewProps<M>) {
  const { t } = useTranslation('patterns');
  const fold = foldSource.useFold(unit);
  const [draft, setDraft] = useState<ListFormRow<M> | null>(null);
  const [editing, setEditing] = useState<ReadonlySet<string>>(new Set());
  // An entry being edited holds the list open, without the header's actions:
  // folding, “+” or Manage would unmount or replace its draft unasked.
  const entryOpen = editing.size > 0 || draft !== null;
  const sectionRef = useRef<HTMLElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  /** An entry that held the focus is gone (saved, cancelled, removed): the focus goes to the “+”. */
  const focusAddNext = useRef(false);
  useLayoutEffect(() => {
    if (!focusAddNext.current || entryOpen) return;
    focusAddNext.current = false;
    const active = document.activeElement;
    // Only when the focus fell out of the list, not when it moved on elsewhere.
    if (
      active === null ||
      active === document.body ||
      sectionRef.current?.contains(active)
    ) {
      addRef.current?.focus();
    }
  });
  /** Unfolded for a jump until the host's fold has caught up: it may write later. */
  const [unfoldedForJump, setUnfoldedForJump] = useState(false);
  const collapsed = fold?.collapsed === true && !entryOpen && !unfoldedForJump;
  const exclusiveEdit = useExclusiveEdit();
  const onEntryEditing = (id: string, isEditing: boolean) =>
    setEditing(ids => {
      if (ids.has(id) === isEditing) return ids;
      const next = new Set(ids);
      if (isEditing) next.add(id);
      else next.delete(id);
      return next;
    });
  const transition = useUnitTransition();
  const items = binding.items;
  const empty = items.length === 0 && draft === null;
  const folds = fold !== null && !empty;
  const showsEntries = !collapsed && !empty;
  const editable = unit.editability.type === 'editable';

  const entryId = (rowKey: string) => listEntryId(unit.id, rowKey);

  // The list itself comes into view with its “+” focused. Its entries hear
  // the signal themselves, but are mounted only while the list is unfolded:
  // it unfolds at once, and the target goes out again to its entries,
  // listening by then. That second emit reaches this listener too, and
  // passes it by.
  const reducedMotion = useReducedMotion();
  useSimpleEventSubscription(editUnitSignal?.addListener, target => {
    if (!isUnitOrEntry(target.unitId, unit.id)) return;
    if (target.unitId === unit.id) {
      if (collapsed) fold?.setCollapsed(false);
      if (editable && !entryOpen) {
        addRef.current?.focus({ preventScroll: true });
      }
      if (sectionRef.current) {
        void revealWhenSettled(
          sectionRef.current,
          reducedMotion ? 'auto' : 'smooth'
        );
      }
      return;
    }
    if (!collapsed) return;
    flushSync(() => setUnfoldedForJump(true));
    fold?.setCollapsed(false);
    editUnitSignal?.emit(target);
  });
  const foldCollapsed = fold?.collapsed === true;
  useEffect(() => {
    if (unfoldedForJump && !foldCollapsed) setUnfoldedForJump(false);
  }, [unfoldedForJump, foldCollapsed]);

  const add = {
    label: unit.add.label,
    editHidden: true,
    ...(unit.add.type === 'single-or-template' && {
      menu: {
        blank: unit.add.blank,
        templates: unit.add.templates.options,
        manage: unit.add.manage,
        usedTemplates: binding.usedAddOptions(items.map(item => item.values))
      }
    })
  };

  return (
    // The list itself is no unit to edit: no frame, its title and “+” only.
    <section
      ref={sectionRef}
      aria-label={unit.title}
      data-testid={`schema-unit-${unit.id}`}
      // Placed as a unit's card is (`UnitLayout`, `compactTitleRow`): its
      // border and padding, cancelled by its margins.
      className="group/list -mx-2 -my-2.5 border border-transparent px-2 py-2.5"
    >
      {/* Entries opening, added or folded ease the height: nothing below jumps. */}
      <AnimatedHeight
        clip="while-resizing"
        transition={transition}
        // Room above and below for the title row's buttons while it eases.
        className="-mx-2 -my-1.5"
        contentClassName="flex flex-col px-2 py-1.5"
      >
        <>
          <div
            className={cn(
              // As high as its buttons, ending where the title does when
              // nothing shows under it (see `UnitLayoutProps.compactTitleRow`).
              '-mt-1.5 flex h-9 items-center justify-between gap-4',
              !showsEntries && '-mb-1.5',
              // Open, the group's title stays in view over its entries, and
              // an entry being edited sticks just below it.
              !collapsed &&
                folds &&
                'sticky top-[var(--unit-sticky-top,0px)] z-2 -mx-2 bg-bg-default px-2 @container-[scroll-state]'
            )}
          >
            {!collapsed && folds && (
              // The shadow under the stuck title: only where `stuck-top` works.
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-full h-1.5 bg-gradient-to-b from-black/5 to-transparent opacity-0 transition-opacity duration-100 stuck-top:opacity-100"
              />
            )}
            <h3 className="flex min-w-0 items-center gap-x-2 text-base font-normal text-fg-muted">
              {icon !== null && <UnitTitleIcon>{icon}</UnitTitleIcon>}
              {fold === null || empty || entryOpen ? (
                <span className="truncate">{unit.title}</span>
              ) : (
                <UnitFoldTitle
                  title={unit.title}
                  fold={fold}
                  summary={String(items.length)}
                />
              )}
              {empty && <EmptyValueMark />}
            </h3>
            {editable && !entryOpen && (
              <div className="flex shrink-0 opacity-0 transition-opacity duration-200 group-hover/list:opacity-100 group-focus-within/list:opacity-100 has-aria-expanded:opacity-100 pointer-coarse:opacity-100">
                {items.length > 0 && (
                  <ListOrderPopover
                    unit={unit}
                    binding={binding}
                    kit={kit}
                    size={size}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      layout="icon"
                      aria-label={t('SchemaForm.list.manage', {
                        title: unit.title
                      })}
                    >
                      <MoreHorizontal />
                    </Button>
                  </ListOrderPopover>
                )}
                <UnitAddButton
                  ref={addRef}
                  add={add}
                  onAdd={templateId =>
                    // Only once a unit being edited elsewhere has let go.
                    exclusiveEdit.requestEdit(() => {
                      fold?.setCollapsed(false);
                      setDraft(newListFormRow(binding.newItem(templateId)));
                    })
                  }
                  onFocus={() => {}}
                />
              </div>
            )}
          </div>
          {showsEntries && (
            <div
              className={cn(
                'flex flex-col gap-4 pt-2',
                // Entries start at the title's text, as a unit's values do.
                icon !== null &&
                  '[--lead-gutter:calc(var(--spacing)*7)] pl-(--lead-gutter)',
                fold !== null && '[--unit-sticky-top:2.25rem]'
              )}
            >
              {items.map((item, index) => {
                const rowKey = rowIdUtils.key(item.rowId);
                const label = `${unit.item.title} ${index + 1}`;
                return (
                  <ListEntry
                    key={rowKey}
                    unit={unit}
                    id={entryId(rowKey)}
                    label={label}
                    row={toListFormRows([item])[0]}
                    kit={kit}
                    variant={variant}
                    size={size}
                    onEditingChange={onEntryEditing}
                    editUnitSignal={editUnitSignal}
                    content={<EntryFields unit={unit} kit={kit} />}
                    onSave={([values]) =>
                      binding.saveItems(
                        binding.items.map(saved =>
                          saved === item && values !== undefined
                            ? { ...saved, values }
                            : saved
                        )
                      )
                    }
                    remove={{
                      label: t('SchemaForm.list.remove'),
                      message: t('SchemaForm.list.removeQuestion', {
                        item: label.toLowerCase()
                      }),
                      run: async () => {
                        await binding.saveItems(
                          binding.items.filter(saved => saved !== item)
                        );
                        focusAddNext.current = true;
                      }
                    }}
                  />
                );
              })}
              {draft !== null && (
                <ListEntry
                  key={draft.rowKey}
                  unit={unit}
                  id={entryId(draft.rowKey)}
                  label={t('SchemaForm.list.newItem', {
                    item: unit.item.title.toLowerCase()
                  })}
                  row={draft}
                  kit={kit}
                  variant={variant}
                  size={size}
                  onEditingChange={onEntryEditing}
                  onClosed={() => {
                    focusAddNext.current = true;
                    setDraft(null);
                  }}
                  isNew
                  content={
                    headsItsRows(unit, kit) ? (
                      <EntryFields unit={unit} kit={kit} />
                    ) : (
                      // Blocks with an empty one at the end: typing there starts the next entry.
                      <ListBlocksField
                        unit={unit}
                        name="items"
                        kit={kit}
                        newItem={binding.newItem}
                      />
                    )
                  }
                  onSave={added =>
                    binding.saveItems([
                      ...binding.items,
                      ...added.map(values => ({
                        rowId: rowIdUtils.local(),
                        values
                      }))
                    ])
                  }
                />
              )}
            </div>
          )}
        </>
      </AnimatedHeight>
    </section>
  );
}

function ListEntry<M extends KindMap<M>>({
  unit,
  id,
  label,
  row,
  kit,
  variant,
  size,
  onEditingChange,
  remove,
  onClosed,
  isNew,
  content,
  onSave,
  editUnitSignal
}: Pick<ListUnitViewProps<M>, 'kit' | 'variant' | 'size' | 'editUnitSignal'> & {
  unit: ListUnit<AnySpec<M>>;
  id: string;
  /** "Rate 2". */
  label: string;
  row: ListFormRow<M>;
  onEditingChange(id: string, isEditing: boolean): void;
  remove?: { label: string; message: string; run(): Promise<void> };
  /** A new entry: it is gone once it leaves edit mode (saved or cancelled). */
  onClosed?(): void;
  isNew?: boolean;
  /** The entry's fields; a new one's may add further entries. */
  content: ReactNode;
  /** The entry's values (a new one's: every entry added). */
  onSave(values: Array<ListItemValues<M>['values']>): Promise<void>;
}) {
  const fields = unit.item.fields;
  const resolver = useFieldsResolver<M, EntryFormValues<M>>(
    data =>
      data.items.flatMap((formRow, index) => {
        const values = readRowValues(fields, formRow.values);
        return fields.map(field => ({
          path: `items.${index}.values.${fieldPath('', field.key)}`,
          spec: field,
          values
        }));
      }),
    kit
  );
  const opened = useRef(false);
  const latestOnEditingChange = useRef(onEditingChange);
  latestOnEditingChange.current = onEditingChange;
  // An entry leaving the list (saved, removed) is no longer being edited.
  useEffect(() => () => latestOnEditingChange.current(id, false), [id]);

  return (
    <SchemaUnitEditor<EntryFormValues<M>>
      unit={{ ...unit, id, title: label }}
      formValues={{ items: [row] }}
      resolver={resolver}
      onSave={data =>
        onSave(fromListFormRows(fields, data.items, []).map(row => row.values))
      }
      content={content}
      required={false}
      groupTitle={false}
      variant={variant}
      size={size}
      editUnitSignal={editUnitSignal}
      entry={{
        isNew,
        remove,
        onEditingChange: isEditing => {
          onEditingChange(id, isEditing);
          if (isEditing) opened.current = true;
          else if (opened.current) onClosed?.();
        }
      }}
    />
  );
}

/**
 * An entry's fields: a kind that heads its own rows (a rate) shows alone,
 * without its label; several fields stack with their labels behind a tile.
 */
function EntryFields<M extends KindMap<M>>({
  unit,
  kit,
  index = 0
}: {
  unit: ListUnit<AnySpec<M>>;
  kit: FormKit<M>;
  /** The entry's row in the form. */
  index?: number;
}) {
  const { mode } = useFieldContext();
  const fields = unit.item.fields;
  const single = fields.length === 1 ? fields[0] : null;
  if (single !== null && headsItsRows(unit, kit)) {
    return (
      <RenderField
        kit={kit}
        spec={single}
        path={fieldPath(`items.${index}.values`, single.key)}
        hideLabel
      />
    );
  }
  if (mode === 'display') {
    // At rest an entry reads as label and value, a line each, under the
    // title's text: the unit's icon already says what the entries are.
    return (
      <div className="grid grid-cols-[fit-content(40%)_minmax(0,1fr)] items-center gap-x-4">
        {fields.map((field, row) => (
          <Fragment key={field.key}>
            <OverflowTooltip title={field.label} align="start">
              <span aria-hidden className="truncate text-fg-muted">
                {field.label}
              </span>
            </OverflowTooltip>
            {/* The first line makes room for the entry's actions while they
                show over its top right corner (hover, focus, touch). */}
            <div
              className={cn(
                'min-w-0',
                row === 0 &&
                  'group-hover:pr-18 group-focus-within:pr-18 pointer-coarse:pr-18'
              )}
            >
              <RenderField
                kit={kit}
                spec={field}
                path={fieldPath(`items.${index}.values`, field.key)}
                hideLabel
              />
            </div>
          </Fragment>
        ))}
      </div>
    );
  }
  return (
    <Block tile={<Layers />}>
      <BlockFields unit={unit} name="items" index={index} kit={kit} />
    </Block>
  );
}

/** The entry is one field whose kind heads its own rows (a rate). */
function headsItsRows<M extends KindMap<M>>(
  unit: ListUnit<AnySpec<M>>,
  kit: FormKit<M>
): boolean {
  const [single, ...rest] = unit.item.fields;
  return (
    single !== undefined &&
    rest.length === 0 &&
    kit[single.kind].RowHeading !== undefined
  );
}

/**
 * The list's order, changed on purpose and out of the way: a popover from
 * the header's reorder button, one line per entry with its handle, and its
 * own Cancel / Save. Closing it any other way (Esc, a click outside) writes
 * nothing; the list on the page never moves meanwhile.
 */
function ListOrderPopover<M extends KindMap<M>>({
  unit,
  binding,
  kit,
  size,
  children
}: Pick<ListUnitViewProps<M>, 'unit' | 'binding' | 'kit' | 'size'> & {
  /** The reorder button. */
  children: ReactElement;
}) {
  const [open, setOpen] = useState(false);
  /** Esc puts a lifted line back first; only the next one closes. */
  const cancelLift = useRef<(() => boolean) | null>(null);
  const titleId = useId();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        aria-labelledby={titleId}
        onEscapeKeyDown={event => {
          if (cancelLift.current?.()) event.preventDefault();
        }}
        // Opens on the first line, where the order's keys work.
        onOpenAutoFocus={event => {
          const first = (
            event.currentTarget as HTMLElement | null
          )?.querySelector<HTMLElement>('[role="row"]');
          if (!first) return;
          event.preventDefault();
          first.focus();
        }}
        className="flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 p-2"
      >
        <header className="flex flex-col gap-0.5 px-1 pt-0.5">
          <p id={titleId} className="text-sm font-medium text-fg-default">
            Manage {unit.title}
          </p>
          <p className="text-xs text-fg-muted">Reorder or remove entries.</p>
        </header>
        {open && (
          <ListOrderForm
            unit={unit}
            binding={binding}
            kit={kit}
            size={size}
            cancelLift={cancelLift}
            onDone={() => setOpen(false)}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function ListOrderForm<M extends KindMap<M>>({
  unit,
  binding,
  kit,
  size,
  cancelLift,
  onDone
}: Pick<ListUnitViewProps<M>, 'unit' | 'binding' | 'kit' | 'size'> & {
  cancelLift: MutableRefObject<(() => boolean) | null>;
  onDone(): void;
}) {
  const form = useForm<EntryFormValues<M>>({
    defaultValues: { items: toListFormRows(binding.items) }
  });
  const save = promiseState.useMutation(async (data: EntryFormValues<M>) => {
    await binding.saveItems(
      fromListFormRows(unit.item.fields, data.items, binding.items)
    );
    onDone();
  });
  const saving = mt.isInProgress(save.state);
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(data => save.track(data))}
        className="flex min-h-0 flex-col gap-2"
      >
        <FieldContextProvider
          value={{
            mode: 'display',
            variant: 'plain',
            size,
            basePath: '',
            ghost: false
          }}
        >
          {/* Room for what overhangs the lines and the scroll box would clip or scroll to: a line's focus ring, and the drop line while dragging (5px above the first line, 9px below the last). */}
          <div className="-mx-1 -mt-2 -mb-3 max-h-80 overflow-y-auto px-1 pt-2 pb-3">
            <EntriesOrder unit={unit} kit={kit} cancelLift={cancelLift} />
          </div>
        </FieldContextProvider>
        {mt.isInError(save.state) && (
          <p role="alert" className="px-1 text-sm text-fg-danger">
            {save.state.error.message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={saving}
            onClick={onDone}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            visuallyDisabled={!form.formState.isDirty}
            disabled={saving}
          >
            {saving && <LoadingSpinner />}
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
}

const ARROW_STEP: Partial<Record<string, -1 | 1>> = {
  ArrowUp: -1,
  ArrowDown: 1
};

/**
 * The entries in the order being set: ↑ / ↓ go between lines and ← / → between
 * a line's controls (the grid list's own keys). Space lifts the focused line,
 * ↑ / ↓ then carry it, Space or Enter puts it down and Esc puts it back where
 * it was lifted from. ⌥↑ / ⌥↓ move it at once, as in a list being edited.
 */
function EntriesOrder<M extends KindMap<M>>({
  unit,
  kit,
  cancelLift
}: {
  unit: ListUnit<AnySpec<M>>;
  kit: FormKit<M>;
  /** Set while a line is lifted: puts it back, `true` when there was one. */
  cancelLift: MutableRefObject<(() => boolean) | null>;
}) {
  const { t } = useTranslation('patterns');
  const ctx = useFieldContext();
  const { fields, move, remove } = useFieldArray<EntryFormValues<M>>({
    name: 'items'
  });
  const [lifted, setLifted] = useState<{ id: string; from: number } | null>(
    null
  );
  const [announcement, setAnnouncement] = useState('');
  const lines = fields.map((field, index) => ({ id: field.id, index }));
  const label = (index: number) => `${unit.item.title} ${index + 1}`;
  const indexOf = (id: string) => fields.findIndex(field => field.id === id);

  const step = (from: number, direction: -1 | 1) => {
    const to = from + direction;
    if (to < 0 || to >= fields.length) return;
    move(from, to);
    setAnnouncement(
      t('SchemaForm.list.moved', { position: to + 1, count: fields.length })
    );
  };

  cancelLift.current =
    lifted === null
      ? null
      : () => {
          const at = indexOf(lifted.id);
          if (at !== -1 && at !== lifted.from) move(at, lifted.from);
          setLifted(null);
          setAnnouncement(
            t('SchemaForm.list.putBack', {
              position: lifted.from + 1,
              count: fields.length
            })
          );
          return true;
        };

  const onKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const row = target.closest<HTMLElement>('[role="row"]');
    if (row === null) return;
    const rows = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('[role="row"]')
    );
    const index = rows.indexOf(row);
    const id = lines[index]?.id;
    if (id === undefined) return;
    const arrow = ARROW_STEP[event.key] ?? null;
    const handled = () => {
      event.preventDefault();
      event.stopPropagation();
    };

    if (lifted !== null) {
      if (arrow !== null) {
        handled();
        step(index, arrow);
      } else if (event.key === ' ' || event.key === 'Enter') {
        handled();
        setLifted(null);
        setAnnouncement(
          t('RowList.dropped', {
            position: index + 1,
            count: fields.length
          })
        );
      }
      return;
    }
    if (arrow !== null && event.altKey) {
      handled();
      step(index, arrow);
      return;
    }
    // Space on the line itself; on its buttons it presses them.
    if (event.key === ' ' && target === row) {
      handled();
      setLifted({ id, from: index });
      setAnnouncement(t('SchemaForm.list.pickedUp', { label: label(index) }));
    }
  };

  return (
    <div
      onKeyDownCapture={onKeyDownCapture}
      onBlur={event => {
        // Focus left the list: the lifted line stays where it is.
        if (!event.currentTarget.contains(event.relatedTarget)) setLifted(null);
      }}
    >
      <DndList
        items={lines}
        getKey={line => line.id}
        getTextValue={line => label(line.index)}
        ariaLabel={unit.title}
        gap="2"
        className="flex flex-col gap-2"
        itemClassName="rounded-lg"
        onReorder={keys => {
          // The new order as moves of the form's rows, one place at a time.
          const order = fields.map(field => field.id);
          keys.forEach((key, to) => {
            const from = order.indexOf(key);
            if (from === to || from === -1) return;
            order.splice(to, 0, ...order.splice(from, 1));
            move(from, to);
          });
        }}
      >
        {line => (
          <DndList.Row
            className={cn(
              'rounded-lg py-1 pr-1 pl-2',
              lifted?.id === line.id &&
                'border-border-accent bg-bg-subtle shadow-md'
            )}
          >
            <div className="flex min-w-0 items-center gap-1">
              {/* One line per entry, as read at rest, fading out where it doesn't fit. */}
              <div
                // Only read here: no stop for the arrows or the pointer.
                ref={element => element?.setAttribute('inert', '')}
                className="min-w-0 flex-1 overflow-hidden px-1.5 [mask-image:linear-gradient(to_right,transparent,#000_0.375rem,#000_calc(100%-1.5rem),transparent)]"
              >
                <FieldContextProvider
                  value={{ ...ctx, mode: 'display', variant: 'plain' }}
                >
                  <EntrySummary unit={unit} kit={kit} index={line.index} />
                </FieldContextProvider>
              </div>
              {/* Without dragging: one place at a time (WCAG 2.5.7). */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                layout="icon"
                aria-label={t('SchemaForm.list.moveUp', {
                  label: label(line.index)
                })}
                disabled={line.index === 0}
                onClick={() => step(line.index, -1)}
              >
                <ArrowUp />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                layout="icon"
                aria-label={t('SchemaForm.list.moveDown', {
                  label: label(line.index)
                })}
                disabled={line.index === fields.length - 1}
                onClick={() => step(line.index, 1)}
              >
                <ArrowDown />
              </Button>
              {/* Gone from the list here; written with the order on Save. */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                layout="icon"
                aria-label={t('RowList.remove', { label: label(line.index) })}
                onClick={() => {
                  if (lifted?.id === line.id) setLifted(null);
                  remove(line.index);
                  setAnnouncement(
                    t('SchemaForm.list.removed', { label: label(line.index) })
                  );
                }}
              >
                <X />
              </Button>
            </div>
          </DndList.Row>
        )}
      </DndList>
      <div aria-live="assertive" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}

/** How many fields of a group a line of the order shows. */
const SUMMARY_FIELDS = 2;

/**
 * An entry on one line: a kind that heads its own rows (a rate) by its
 * heading, else its first fields side by side without their labels.
 */
function EntrySummary<M extends KindMap<M>>({
  unit,
  kit,
  index
}: {
  unit: ListUnit<AnySpec<M>>;
  kit: FormKit<M>;
  index: number;
}) {
  const fields = unit.item.fields;
  const single = fields.length === 1 ? fields[0] : null;
  const heading =
    single === null
      ? null
      : kindHeading(
          kit,
          single,
          fieldPath(`items.${index}.values`, single.key)
        );
  if (heading !== null) return heading;
  return (
    <span className="flex h-8 min-w-0 items-center gap-2 overflow-hidden">
      <Layers aria-hidden className="size-4 shrink-0 text-fg-muted" />
      {fields.slice(0, SUMMARY_FIELDS).map(field => (
        <span
          key={field.key}
          // A value longer than its share fades out instead of being cut.
          className="flex max-h-8 min-w-0 flex-1 items-center overflow-hidden [mask-image:linear-gradient(to_right,#000_calc(100%-1.5rem),transparent)]"
        >
          <RenderField
            kit={kit}
            spec={field}
            path={fieldPath(`items.${index}.values`, field.key)}
            hideLabel
          />
        </span>
      ))}
    </span>
  );
}
